import React from 'react';
import { DESTINATION_TYPES } from '../../../containers/destination/constants/destinationTypes';
import { PipelineAPI } from '../../../edge/pipelines/api/PipelineAPI';
import { PIPELINE_STATUS_DATA_MAP } from '../../../edge/pipelines/constants/pipelineStatus';
import { RawIntegration } from '../../../edge/pipelines/interfaces/integration';
import { RawSourceType as EnterpriseRawSourceType } from '../../../edge/sources/interfaces/source';
import { GlobalSearchItem, GlobalSearchRegistryItem } from '../../interfaces/global-search';
import { edgeRoute, standardRoute } from '../../../containers/pipeline/constants/pipelineRoutes';
import PipelinesAPI from '../../../containers/pipeline/api/PipelinesAPI';
import { RawPipeline } from '../../../containers/pipeline/interfaces/model';
import { RawSourceType } from '../../../containers/pipeline/select-source-type/interfaces/sourceType';
import { SOURCE_TYPES } from '../../../containers/pipeline/interfaces/source-type';
import { GenericListItemContent, GenericListItemContentProps } from './GenericListItemContent/GenericListItemContent';
import { GlobalSearchEntityCommonData } from './interfaces/global-search-entity';
import { isH2OEnabled, isUnifiedViewEnabled } from './registryTeamSettings';
import { UnifiedPipelinesAPI } from '../../../containers/pipeline/pipeline-list/unified/api/UnifiedPipelinesAPI';
import { getUnifiedPipelineDetailPath } from '../../../containers/pipeline/pipeline-list/unified/unifiedPipelineListConstants';
import { UNIFIED_STATUS_DATA_MAP } from '../../../containers/pipeline/constants/unifiedPipelineStatus';
import {
  UnifiedPipeline,
  UnifiedPipelineEngine,
  UnifiedPipelineRaw
} from '../../../containers/pipeline/pipeline-list/unified/interfaces/unifiedPipeline';
import { encodeUnifiedId, mapUnifiedPipelines } from '../../../containers/pipeline/pipeline-list/unified/unifiedPipelineMapper';

export interface GlobalSearchPipelineData {
  status: string;
  /** Edge native id — set by the edge factory for action/routing consumers. */
  id?: number;
  destination: GlobalSearchEntityCommonData;
  source: GlobalSearchEntityCommonData;
  /** Same engine-encoded id the unified listing displays: standard "548-S", edge plain "548". */
  unifiedId: string;
}

export const GLOBAL_SEARCH_PIPELINE_KEY = 'pipelines';

export const GlobalSearchPipelineRegistry: GlobalSearchRegistryItem<GlobalSearchPipelineData> = {
  key: GLOBAL_SEARCH_PIPELINE_KEY,
  title: 'Pipelines',
  maxItemsWhenNoFiltersApplied: 2,
  maxItemsWhenFiltersApplied: 20,
  sortOrder: 1,
  filters: [
    {
      value: GLOBAL_SEARCH_PIPELINE_KEY,
      label: 'Pipelines'
    }
  ],
  viewAllRoute: '/pipeline',
  inMemoryFiltering: true,
  dataResolver: () => {
    // Unified view: ONE call to the unified middleware instead of the two
    // per-engine APIs — global search then reflects exactly what the unified
    // listing shows (same rows, same #IDs, engine invisible).
    if (isUnifiedViewEnabled()) {
      return fetchUnifiedPipelines().then(raw =>
        mapUnifiedPipelines(raw)
          // same completeness rules as the legacy per-engine paths: rows without
          // a routable id or a configured source+destination are not search hits
          .filter(row => !!row.seq_id && !!row.source && !!row.destination)
          .map(GlobalSearchUnifiedPipelineFactory)
      );
    }

    const isH20Enabled = isH2OEnabled();

    const promises = [
      PipelinesAPI.getIntegrations(undefined, false)
    ];

    if (isH20Enabled) {
      promises.push(PipelineAPI.getIntegrations());
    }

    return Promise.all(promises)
      .then(([{ data: standardPipelines }, edgePipelineRes]) => {
        const _edgePipelines = isH20Enabled
          ? edgePipelineRes.data
              .filter((p) => !!p.source && !!p.destination)
              .map(GlobalSearchEnterprisePipelineFactory)
          : [];

        const _standardPipelines = standardPipelines.map(GlobalSearchPipelineFactory);

        return [ ..._edgePipelines, ..._standardPipelines ];
      });
  },
  renderItem: (item) => {
    // Unified view: the engine is invisible (no Edge badge) and rows carry the
    // same engine-encoded #ID the unified pipelines table shows.
    const isUnifiedPipelineViewEnabled = isUnifiedViewEnabled();

    const props: GenericListItemContentProps = {
      suffixIcon: item.icon,
      suffixIconBadge: !isUnifiedPipelineViewEnabled && item.data.source.isEdge ? 'Edge' : undefined,
      primaryText:
        isUnifiedPipelineViewEnabled && item.data.unifiedId ? (
          <>
            {item.name}
            <span className='text-secondary ml-1'>(#{item.data.unifiedId})</span>
          </>
        ) : (
          item.name
        ),
      primaryTooltipText: item.name,
      primaryLogoTooltip: item.data.source.displayType,
      primaryLogoProps: {
        logoURL: item.data.source.logoUrl,
        darkModeLogoURL: item.data.source.darkModeLogoUrl,
        primaryColor: SOURCE_TYPES[item.data.source.type]?.primaryColor,
        darkModePrimaryColor: SOURCE_TYPES[item.data.source.type]?.darkModePrimaryColor
      },
      logoConnector: true,
      logoConnectorColor: item.data.status,
      secondaryText: item.data.destination.name,
      secondaryLogoTooltip: item.data.destination.displayType,
      secondaryLogoProps: {
        logoURL: item.data.destination.logoUrl,
        darkModeLogoURL: item.data.destination.darkModeLogoUrl,
        primaryColor: DESTINATION_TYPES[item.data.destination.type]?.primaryColor,
        darkModePrimaryColor: DESTINATION_TYPES[item.data.destination.type]?.darkModePrimaryColor
      }
    };

    return (
      <GenericListItemContent {...props} />
    );
  }
};

function fetchUnifiedPipelines(): Promise<UnifiedPipelineRaw[]> {
  // Full list in one GET (deleted excluded); global search filters in memory.
  return UnifiedPipelinesAPI.getPipelinesV2().then(res => res.data);
}

/** Search item from a unified listing row — same #ID, routing, and status color the listing shows. */
export function GlobalSearchUnifiedPipelineFactory(
  row: UnifiedPipeline
): GlobalSearchItem<GlobalSearchPipelineData> {
  const isEdge = row.engine === UnifiedPipelineEngine.EDGE;

  return {
    data: {
      status: UNIFIED_STATUS_DATA_MAP[row.status].colorType,
      unifiedId: row.unified_id,
      source: {
        name: row.source?.name ?? '',
        displayType: row.source?.type_display ?? '',
        type: row.source?.type ?? '',
        logoUrl: row.source?.logo_url ?? '',
        darkModeLogoUrl: row.source?.dark_mode_logo_url ?? row.source?.logo_url ?? '',
        isEdge
      },
      destination: {
        name: row.destination?.name ?? '',
        displayType: row.destination?.type_display ?? '',
        type: row.destination?.type ?? '',
        logoUrl: row.destination?.logo_url ?? '',
        darkModeLogoUrl: row.destination?.dark_mode_logo_url ?? row.destination?.logo_url ?? ''
      }
    },
    key: `unified-pipeline-${row.unified_id}`,
    name: row.name,
    exitURL: getUnifiedPipelineDetailPath(row),
    icon: 'pipeline',
    searchKeywords: [
      row.name,
      row.source?.name,
      row.source?.type,
      row.source?.type_display,
      row.destination?.name,
      row.destination?.type,
      row.destination?.type_display,
      String(row.seq_id),
      row.unified_id
    ].filter(Boolean)
  };
}

export function GlobalSearchPipelineFactory(
  rawPipeline: RawPipeline
): GlobalSearchItem<GlobalSearchPipelineData> {
  return {
    data: {
      status: rawPipeline.pipeline_status.color_type,
      unifiedId: encodeUnifiedId(UnifiedPipelineEngine.STANDARD, rawPipeline.seq_id),
      source: GlobalSearchSourceFactory(rawPipeline as unknown as RawSourceType),
      destination: {
        name: rawPipeline.dest_name,
        type: rawPipeline.dest_type,
        displayType: rawPipeline.dest_type_display,
        logoUrl: rawPipeline.dest_type_logo_url,
        darkModeLogoUrl: rawPipeline.dest_type_dark_mode_logo_url ?? rawPipeline.dest_type_logo_url
      }
    },
    key: `${rawPipeline.source_type}-standard-pipeline-${rawPipeline.seq_id}`,
    name: rawPipeline.source_name,
    exitURL: standardRoute.overview(rawPipeline.seq_id),
    icon: 'pipeline',
    // No "-S" keyword here: this factory serves the FLAG-OFF (tabbed) path only —
    // the suffix is a unified-view concept and must not match on tabbed teams.
    // The unified resolver builds its own items with unified-id keywords.
    searchKeywords: [
      rawPipeline.source_name,
      rawPipeline.dest_name,
      rawPipeline.source_type,
      rawPipeline.dest_type,
      String(rawPipeline.seq_id)
    ]
  };
}

export function GlobalSearchEnterprisePipelineFactory(
  rawPipeline: RawIntegration
): GlobalSearchItem<GlobalSearchPipelineData> {
  return {
    data: {
      id: rawPipeline.id,
      status: PIPELINE_STATUS_DATA_MAP[rawPipeline.status.type].colorType,
      unifiedId: encodeUnifiedId(UnifiedPipelineEngine.EDGE, rawPipeline.seq_id),
      source: GlobalSearchEnterpriseSourceFactory(rawPipeline.source.source_type),
      destination: {
        name: rawPipeline.destination.name,
        type: rawPipeline.destination.destination_type.type,
        displayType: rawPipeline.destination.destination_type.display_name,
        logoUrl: rawPipeline.destination.destination_type.logo_url,
        darkModeLogoUrl: rawPipeline.destination.destination_type.dark_mode_logo_url
      }
    },
    key: `${rawPipeline.source.source_type.type}-edge-pipeline-${rawPipeline.id}`,
    name: rawPipeline.name,
    exitURL: edgeRoute.jobHistory(rawPipeline.id),
    icon: 'pipeline',
    searchKeywords: [
      rawPipeline.name,
      rawPipeline.source.name,
      rawPipeline.source.source_type.type,
      rawPipeline.source.source_type.display_name,
      rawPipeline.destination.name,
      rawPipeline.destination.destination_type.type,
      rawPipeline.destination.destination_type.display_name,
      String(rawPipeline.seq_id)
    ]
  };
}

export function GlobalSearchEnterpriseSourceFactory(
  rawSource: EnterpriseRawSourceType
) {
  return {
    name: rawSource.display_name,
    displayType: rawSource.display_name,
    type: rawSource.type,
    logoUrl: rawSource.logo_url,
    darkModeLogoUrl: rawSource.dark_mode_logo_url,
    isEdge: true
  };
}

export function GlobalSearchSourceFactory(
  rawSource: RawSourceType
) {
  return {
    name: rawSource.source_name,
    displayType: rawSource.source_type_display,
    type: rawSource.source_type,
    logoUrl: rawSource.source_type_logo_url,
    darkModeLogoUrl: rawSource.source_type_dark_mode_logo_url ?? rawSource.source_type_logo_url,
    isEdge: false,
  };
}
